/**
 * Internal API client for Newsdesk backend
 */

import accountService from './accountService';

const API_BASE = '/api';

class ApiClient {
  async request(path, options = {}) {
    const url = `${API_BASE}${path}`;
    const response = await accountService.authenticatedFetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
    });

    const data = await response.json();
    if (!response.ok) {
      throw new Error(data.error || `Request failed: ${response.status}`);
    }
    return data;
  }

  get(path) { return this.request(path); }

  post(path, body) {
    return this.request(path, { method: 'POST', body: JSON.stringify(body) });
  }

  put(path, body) {
    return this.request(path, { method: 'PUT', body: JSON.stringify(body) });
  }

  patch(path, body) {
    return this.request(path, { method: 'PATCH', body: JSON.stringify(body) });
  }

  del(path) {
    return this.request(path, { method: 'DELETE' });
  }

  // Dashboard
  getStats() { return this.get('/dashboard/stats'); }
  getRecentActivity() { return this.get('/dashboard/recent-activity'); }
  getSourceHealth() { return this.get('/dashboard/source-health'); }
  getTrending() { return this.get('/dashboard/trending'); }

  // Sources
  getSources(params = '') { return this.get(`/sources${params ? '?' + params : ''}`); }
  getSource(id) { return this.get(`/sources/${id}`); }
  createSource(data) { return this.post('/sources', data); }
  updateSource(id, data) { return this.put(`/sources/${id}`, data); }
  deleteSource(id) { return this.del(`/sources/${id}`); }
  testSource(id) { return this.post(`/sources/${id}/test`); }
  fetchSource(id) { return this.post(`/sources/${id}/fetch`); }

  // News Items
  getNewsItems(params = '') { return this.get(`/news-items${params ? '?' + params : ''}`); }
  getNewsItem(id) { return this.get(`/news-items/${id}`); }
  analyzeNewsItem(id) { return this.post(`/news-items/${id}/analyze`); }
  discardNewsItem(id) { return this.post(`/news-items/${id}/discard`); }
  deleteNewsItem(id) { return this.del(`/news-items/${id}`); }

  // Clusters
  getClusters(params = '') { return this.get(`/clusters${params ? '?' + params : ''}`); }
  getCluster(id) { return this.get(`/clusters/${id}`); }
  generateFromCluster(id) { return this.post(`/clusters/${id}/generate`); }
  removeItemFromCluster(clusterId, itemId) { return this.post(`/clusters/${clusterId}/remove-item/${itemId}`); }
  deleteCluster(id) { return this.del(`/clusters/${id}`); }
  reclusterAll() { return this.post('/clusters/recluster'); }

  // Articles
  getArticles(params = '') { return this.get(`/articles${params ? '?' + params : ''}`); }
  getArticle(id) { return this.get(`/articles/${id}`); }
  updateArticle(id, data) { return this.put(`/articles/${id}`, data); }
  approveArticle(id) { return this.post(`/articles/${id}/approve`); }
  publishArticle(id) { return this.post(`/articles/${id}/publish`); }
  rejectArticle(id) { return this.post(`/articles/${id}/reject`); }
  archiveArticle(id) { return this.post(`/articles/${id}/archive`); }
  regenerateArticle(id) { return this.post(`/articles/${id}/regenerate`); }
  deleteArticle(id) { return this.del(`/articles/${id}`); }

  // API Keys
  getApiKeys() { return this.get('/api-keys'); }
  getApiKey(id) { return this.get(`/api-keys/${id}`); }
  createApiKey(data) { return this.post('/api-keys', data); }
  updateApiKey(id, data) { return this.put(`/api-keys/${id}`, data); }
  deleteApiKey(id) { return this.del(`/api-keys/${id}`); }

  // Users
  getUsers() { return this.get('/users'); }
  getUser(id) { return this.get(`/users/${id}`); }
  updateUser(id, data) { return this.put(`/users/${id}`, data); }
  deactivateUser(id) { return this.del(`/users/${id}`); }

  // Settings
  getSettings() { return this.get('/settings'); }
  updateSettings(settings) { return this.put('/settings', { settings }); }

  // Jobs
  getJobs(params = '') { return this.get(`/jobs${params ? '?' + params : ''}`); }
  cancelJob(id) { return this.post(`/jobs/${id}/cancel`); }
  clearCompletedJobs() { return this.post('/jobs/clear-completed'); }

  // Activity
  getActivity(params = '') { return this.get(`/activity${params ? '?' + params : ''}`); }

  // Pipeline
  runPipeline() { return this.post('/pipeline/run'); }

  // Housekeeping
  runHousekeeping() { return this.post('/housekeeping/run'); }

  // Auth init
  initUser() { return this.post('/auth/init'); }
}

const api = new ApiClient();
export default api;
